import { Adresse } from "codegen";
import { addMonths, startOfMonth } from "date-fns";

import { TotalDistribution, toMonthKey } from "pages/Sikring/sikringUtils";
import { SikringFlyt, SikringOmfangType } from "pages/Sikring/types";
import { SpotpriceArea } from "utils/SpotpriceArea";

/**
 * Versjoner av opprett-flyten, mens signering og kontrakt rulles ut gradvis.
 * - 0.1: bare sikring. Signering og kontrakt er låst og avkrysset.
 * - 0.2: kontrakt kan opprettes. Signering er fortsatt låst og avkrysset.
 * - 1.0: alt er åpent, også signering og innkjøpsbestilling til NTE Energi.
 * Før 1.0 er innkjøpsbestillingen låst og avkrysset (sendes ikke).
 */
export enum SikringFlowVersion {
	V0_1 = "0.1",
	V0_2 = "0.2",
	V1_0 = "1.0",
}

/**
 * Styres av VITE_SIKRING_FLOW_VERSION, som settes per miljø ved bygg. Ukjent eller manglende verdi gir 0.1,
 * så prod er låst som standard.
 */
export const SIKRING_FLOW_VERSION =
	Object.values(SikringFlowVersion).find((version) => version === import.meta.env.VITE_SIKRING_FLOW_VERSION) ?? SikringFlowVersion.V0_1;

export const SIGNING_LOCKED = SIKRING_FLOW_VERSION !== SikringFlowVersion.V1_0;
export const CONTRACT_LOCKED = SIKRING_FLOW_VERSION === SikringFlowVersion.V0_1;
export const ENERGI_MAIL_LOCKED = SIKRING_FLOW_VERSION !== SikringFlowVersion.V1_0;
/** Sikring på målepunktnivå er ikke i salg ennå, så bare kundenivå kan velges. */
export const MAALEPUNKT_LOCKED = true;

/** Steg i veiviseren. Kontraktsteget hoppes over når ordren ikke skal ha kontrakt. */
export enum WizardStep {
	Kunde = "kunde",
	Kontrakt = "kontrakt",
	PeriodeVolumPris = "periodeVolumPris",
	Kontroller = "kontroller",
}

export const STEP_NAMES: Record<WizardStep, string> = {
	[WizardStep.Kunde]: "Kunde og omfang",
	[WizardStep.Kontrakt]: "Kontrakt",
	[WizardStep.PeriodeVolumPris]: "Periode, volum og pris",
	[WizardStep.Kontroller]: "Kontroller og opprett",
};

export interface WizardState {
	skipSigning: boolean;
	skipContract: boolean;
	scope: SikringOmfangType;
	maalepunktId: string;
	prisomraade: SpotpriceArea;
	/** Kundenivå: målepunkt-ID-er lagt til for hånd, i tillegg til målepunktene på kundens aktive kontrakter. */
	addedMaalepunktIds: string[];
	/** Kundenivå: målepunkt-ID-er brukeren har ekskludert fra sikringen. */
	excludedMaalepunktIds: string[];
	/**
	 * Perioden oppgis som hele måneder (yyyy-MM), fordi backend krever at sikringen starter
	 * første dag i en måned og slutter siste dag i en måned. Begge er inklusive.
	 */
	startMonth: string;
	endMonth: string;
	volumeMode: "total" | "monthly";
	/** Hvordan totalvolumet fordeles på månedene. Brukes bare når volumeMode er "total". */
	totalDistribution: TotalDistribution;
	totalVolume: string;
	volumePerMonth: Record<string, string>;
	innpris: string;
	/** Summen kunden betaler. Sikringsmarginen beregnes som kundepris - innpris. */
	kundepris: string;
	skipEnergiMail: boolean;
	/** Kontaktperson på ordren. Bedriftssøket gir bare orgnr, navn og adresse. */
	contactFirstName: string;
	contactLastName: string;
	contactMobile: string;
	contactEmail: string;
	contactFodselsnummer: string;
	/** Påslag på hovedproduktet, i øre/kWh. Påkrevd når ordren har kontrakt. */
	paaslag: string;
	/** Fast beløp på hovedproduktet, i kroner. Påkrevd når ordren har kontrakt. */
	fastbelop: string;
	/** Produktet kunden går over til etter sikringen. Påkrevd når ordren har kontrakt. */
	followupTariffId: string;
	/**
	 * Verdier for oppfølgingsproduktets interaktive felter (prisvariabler i UtilityCloud, f.eks. i:Margin_pris),
	 * med feltnavnet som nøkkel. Kan inneholde verdier fra et tidligere valgt produkt, så bare feltene til
	 * produktet som er valgt nå skal brukes.
	 */
	interactiveFieldValues: Record<string, string>;
	/** Settes til bedriftsadressen når bedriften velges. Null mens brukeren skriver inn en ny uten å ha valgt fra listen. */
	invoiceAddress: Adresse | null;
	/**
	 * overlapKey for de overlappende sikringene brukeren har bekreftet at de vil fortsette med. Lagres som
	 * nøkkel i stedet for boolsk verdi, slik at bekreftelsen faller bort av seg selv når overlappen endres.
	 */
	overlapAcknowledgedFor: string;
	confirmed: boolean;
}

export function createInitialWizardState(): WizardState {
	const nextMonth = addMonths(startOfMonth(new Date()), 1);
	return {
		skipSigning: SIGNING_LOCKED,
		skipContract: CONTRACT_LOCKED,
		scope: MAALEPUNKT_LOCKED ? SikringOmfangType.Kunde : SikringOmfangType.Maalepunkt,
		maalepunktId: "",
		addedMaalepunktIds: [],
		excludedMaalepunktIds: [],
		// NTE sine kunder ligger stort sett i Midt-Norge.
		prisomraade: SpotpriceArea.NO3,
		startMonth: toMonthKey(nextMonth),
		endMonth: toMonthKey(addMonths(nextMonth, 11)),
		volumeMode: "total",
		totalDistribution: TotalDistribution.Standard,
		totalVolume: "",
		volumePerMonth: {},
		innpris: "",
		kundepris: "",
		skipEnergiMail: ENERGI_MAIL_LOCKED,
		contactFirstName: "",
		contactLastName: "",
		contactMobile: "",
		contactEmail: "",
		contactFodselsnummer: "",
		paaslag: "",
		fastbelop: "",
		followupTariffId: "",
		interactiveFieldValues: {},
		invoiceAddress: null,
		overlapAcknowledgedFor: "",
		confirmed: false,
	};
}

/** Signering uten kontrakt gir ingen mening, så uten kontrakt er det heller ingen signering. */
export function skipsSigning(wiz: Pick<WizardState, "skipSigning" | "skipContract">): boolean {
	return wiz.skipSigning || wiz.skipContract;
}

export function getFlyt(wiz: Pick<WizardState, "skipSigning" | "skipContract">): SikringFlyt {
	if (wiz.skipContract) return SikringFlyt.KunSikring;
	return wiz.skipSigning ? SikringFlyt.KontraktSikring : SikringFlyt.SigneringKontraktSikring;
}

/**
 * Om brukeren kan endre målepunktlisten på kundenivå. Uten kontrakt gjelder sikringen alle målepunktene NTE allerede
 * leverer til, altså kundens eksisterende, så da kan ingen legges til eller ekskluderes.
 */
export function canEditMaalepunkter(wiz: Pick<WizardState, "scope" | "skipContract">): boolean {
	return wiz.scope === SikringOmfangType.Kunde && !wiz.skipContract;
}

/**
 * Målepunkt lagt til for hånd og målepunkt ekskludert, bare når listen kan endres. Valgene beholdes i state, så de
 * kommer tilbake om kontrakten slås på igjen.
 */
export function getAddedMaalepunktIds(wiz: Pick<WizardState, "scope" | "skipContract" | "addedMaalepunktIds">): string[] {
	return canEditMaalepunkter(wiz) ? wiz.addedMaalepunktIds : [];
}

export function getExcludedMaalepunktIds(wiz: Pick<WizardState, "scope" | "skipContract" | "excludedMaalepunktIds">): string[] {
	return canEditMaalepunkter(wiz) ? wiz.excludedMaalepunktIds : [];
}

/** Kontaktpersonen trengs bare for kontrakten. Uten kontrakt (KUN_SIKRING) er feltene valgfrie og låst. */
export function requiresContact(wiz: Pick<WizardState, "skipSigning" | "skipContract">): boolean {
	return getFlyt(wiz) !== SikringFlyt.KunSikring;
}

/** Stegets faste nummer i veiviseren. Alle stegene har nummer, også de flyten hopper over. */
export function getStepNumber(step: WizardStep): number {
	return Object.values(WizardStep).indexOf(step) + 1;
}

export function getStepByNumber(stepNumber: number): WizardStep | undefined {
	return Object.values(WizardStep)[stepNumber - 1];
}

/** Stegene ordren går gjennom. Uten kontrakt er det ingen kontraktsopplysninger å fylle ut. */
export function getActiveSteps(wiz: Pick<WizardState, "skipContract">): WizardStep[] {
	return Object.values(WizardStep).filter((step) => step !== WizardStep.Kontrakt || !wiz.skipContract);
}
